/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Square, MoveUpRight, Pencil, MousePointer, Trash2, Check, MessageSquarePlus, Play, Pause, Circle, Paperclip, X, SkipBack, SkipForward, ChevronLeft, ChevronRight, Rewind, FastForward } from 'lucide-react';

/**
 * MediaAnnotator
 * ==============
 * Frame.io-style annotation layer for a single image OR video.
 *
 * Props:
 *   src          — media URL
 *   type         — 'image' | 'video'
 *   annotations  — array of {id, author_name, text, timecode_sec, shapes:[...], point, resolved, created_at}
 *   readOnly     — disable tool palette + adding notes (still lets you click pins)
 *   onAdd        — (payload) => Promise; payload has {text, timecode_sec, shapes, point, frame_image_url?}
 *   onResolve    — (id) => Promise
 *   onDelete     — (id) => Promise
 *
 * All shape coordinates are NORMALIZED to 0..1 of the media native box so
 * they render correctly regardless of container size.
 */
import { displayNameFor, shouldRenderHandlePill, handleFor } from '@/lib/displayName';

export default function MediaAnnotator({
  src, type = 'image', annotations = [], readOnly = false,
  onAdd, onResolve, onDelete, viewerRole = '',
  // Controlled pin-selection — parent passes activeAnnotationId so clicks on
  // pins/timeline-markers can scroll the parent's notes pane to the matching
  // row. If not provided, the component falls back to internal state.
  activeAnnotationId = null,
  // ``seekTick`` (optional) — a monotonically increasing counter the parent
  // bumps on every "seek again" intent. Lets a repeated click on the same
  // annotation still re-seek the video (without this, React skips the
  // effect because activeAnnotationId didn't change).
  seekTick = 0,
  onActiveAnnotationChange = null,
}) {
  const containerRef = useRef(null);
  const mediaRef = useRef(null);
  const overlayRef = useRef(null);
  const [size, setSize] = useState({ w: 960, h: 540 });
  const [tool, setTool] = useState('select'); // select | rect | arrow | freehand | pin
  const [color] = useState('#fb7185');
  const [draft, setDraft] = useState(null);
  const [noteDraft, setNoteDraft] = useState(null); // { shapes, point, timecode_sec, text }
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [internalOpenPinId, setInternalOpenPinId] = useState(null);
  // Controlled-vs-uncontrolled bridge: when parent supplies activeAnnotationId
  // we use that as the source of truth; otherwise local state.
  const openPinId = activeAnnotationId ?? internalOpenPinId;
  const setOpenPinId = (id) => {
    setInternalOpenPinId(id);
    onActiveAnnotationChange?.(id);
  };

  // Keep overlay sized to the actual rendered media
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [src]);

  // Hook up video events
  useEffect(() => {
    if (type !== 'video') return;
    const el = mediaRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime);
    const onLoad = () => setDuration(el.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onLoad);
    el.addEventListener('durationchange', onLoad);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    // If metadata was already loaded by the time we wired up (cached
    // reload, fast network), the loadedmetadata event won't fire again.
    // Read duration synchronously so timeline pins render immediately.
    if (el.readyState >= 1 && el.duration) setDuration(el.duration);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onLoad);
      el.removeEventListener('durationchange', onLoad);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
    };
  }, [type, src]);

  // When the parent controls activeAnnotationId (e.g. user clicked a note in
  // the right pane), seek the video to that note's timecode so they see the
  // exact frame the client annotated.
  //
  // Robustness fixes (May 2026 founder report — "video does not move when we
  // click the correction note"):
  //   1. Re-fire on EVERY click — the parent passes a monotonically
  //      increasing ``seekTick``, so the effect runs even when the same
  //      annotation is clicked twice.
  //   2. If video metadata hasn't loaded yet (``readyState < 1``), defer the
  //      seek via the ``loadedmetadata`` event — otherwise some browsers
  //      silently drop the ``currentTime`` assignment.
  //   3. Pause briefly before seeking so the user sees the exact frame.
  useEffect(() => {
    if (!activeAnnotationId) return;
    const ann = annotations.find(a => a.id === activeAnnotationId);
    if (!ann) return;
    if (type !== 'video' || ann.timecode_sec == null) return;
    const v = mediaRef.current;
    if (!v) return;
    const doSeek = () => {
      try {
        if (!v.paused) v.pause();
        v.currentTime = ann.timecode_sec;
      } catch { /* noop */ }
    };
    if (v.readyState >= 1) {
      doSeek();
    } else {
      v.addEventListener('loadedmetadata', doSeek, { once: true });
    }
  }, [activeAnnotationId, seekTick, annotations, type]);

  // Keyboard transport shortcuts — frame-by-frame review is critical for
  // animatic critique. Active only when the annotator is mounted AND the
  // user isn't typing into a textarea/input (so it doesn't fight composer).
  useEffect(() => {
    if (type !== 'video') return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isEditing = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (isEditing) return;
      const v = mediaRef.current;
      if (!v) return;
      const FRAME = 1 / 24; // assume 24fps for now — can be made dynamic later
      switch (e.key) {
        case ' ':
        case 'k':
        case 'K':
          e.preventDefault();
          if (v.paused) v.play(); else v.pause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          v.currentTime = Math.max(0, v.currentTime - FRAME);
          break;
        case 'ArrowRight':
          e.preventDefault();
          v.currentTime = Math.min(v.duration || 0, v.currentTime + FRAME);
          break;
        case 'j':
        case 'J':
          e.preventDefault();
          v.currentTime = Math.max(0, v.currentTime - 5);
          break;
        case 'l':
        case 'L':
          e.preventDefault();
          v.currentTime = Math.min(v.duration || 0, v.currentTime + 5);
          break;
        case 'Home':
          e.preventDefault();
          v.currentTime = 0;
          break;
        case 'End':
          e.preventDefault();
          if (v.duration) v.currentTime = v.duration;
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [type]);

  // Filter annotations relevant to the current timecode (for video)
  const visibleAnnotations = useMemo(() => {
    if (type === 'image') return annotations;
    // For video: show ones within ±0.3s of the current frame while paused, always on timeline
    return annotations;
  }, [annotations, type, currentTime]);

  const toLocal = (ev) => {
    const r = overlayRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (ev.clientY - r.top) / r.height)),
    };
  };

  const startDraw = (ev) => {
    if (readOnly || tool === 'select') return;
    const p = toLocal(ev);
    if (tool === 'pin') {
      // immediately open note composer at this pin
      setNoteDraft({
        shapes: [],
        point: p,
        timecode_sec: type === 'video' ? currentTime : null,
        text: '',
      });
      return;
    }
    if (tool === 'freehand') setDraft({ kind: 'freehand', points: [p], color });
    else if (tool === 'rect') setDraft({ kind: 'rect', x1: p.x, y1: p.y, x2: p.x, y2: p.y, color });
    else if (tool === 'arrow') setDraft({ kind: 'arrow', x1: p.x, y1: p.y, x2: p.x, y2: p.y, color });
  };

  const moveDraw = (ev) => {
    if (!draft) return;
    const p = toLocal(ev);
    if (draft.kind === 'freehand') setDraft({ ...draft, points: [...draft.points, p] });
    else setDraft({ ...draft, x2: p.x, y2: p.y });
  };

  const endDraw = () => {
    if (!draft) return;
    // Open note composer with this one shape attached
    setNoteDraft({
      shapes: [draft],
      point: null,
      timecode_sec: type === 'video' ? currentTime : null,
      text: '',
    });
    setDraft(null);
    setTool('select');
  };

  const saveNote = async () => {
    if (!noteDraft) return;
    if (!onAdd) return;
    // Pause video before save so client stays on the annotated frame
    if (type === 'video' && mediaRef.current) mediaRef.current.pause();
    await onAdd({ ...noteDraft });
    setNoteDraft(null);
  };

  const fmtTime = (t) => {
    if (!t && t !== 0) return '';
    const m = Math.floor(t / 60).toString().padStart(2, '0');
    const s = Math.floor(t % 60).toString().padStart(2, '0');
    const ms = Math.floor((t % 1) * 100).toString().padStart(2, '0');
    return `${m}:${s}.${ms}`;
  };

  // ═══ TIMECODE CLUSTERS ═══
  // Group annotations whose timecodes fall within ±1 second of each other so
  // we can surface a "🔴 14 notes on opening frame" badge above the player.
  // For images (no timecode), all annotations collapse into one cluster.
  const clusters = useMemo(() => {
    if (!annotations.length) return [];
    if (type !== 'video') {
      return annotations.length >= 3
        ? [{ key: 'all', t: 0, label: type === 'image' ? 'on this frame' : 'on this clip', items: annotations }]
        : [];
    }
    const sorted = [...annotations].sort(
      (a, b) => (a.timecode_sec ?? 0) - (b.timecode_sec ?? 0),
    );
    const buckets = [];
    let cur = null;
    for (const a of sorted) {
      const t = a.timecode_sec ?? 0;
      if (!cur || Math.abs(t - cur.t) > 1) {
        cur = { key: `${t.toFixed(2)}-${a.id}`, t, items: [] };
        buckets.push(cur);
      }
      cur.items.push(a);
    }
    // Only surface clusters of 3+ (otherwise the badge strip is noise)
    return buckets
      .filter(b => b.items.length >= 3)
      .map(b => ({ ...b, label: b.t < 0.5 ? 'on opening frame' : `at ${fmtTime(b.t)}` }));
  }, [annotations, type]);
  const unresolvedCount = (items) => items.filter(x => !x.resolved).length;

  // ═══ RENDER ═══
  return (
    <div className="flex flex-col gap-3" data-testid="media-annotator">
      {/* Timecode cluster badges — surface stacked annotations so clients can
          jump straight to a wall of notes at the same frame. */}
      {clusters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="cluster-badges">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mr-1">
            Stacked notes
          </span>
          {clusters.map(c => {
            const unresolved = unresolvedCount(c.items);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => {
                  if (type === 'video' && mediaRef.current) {
                    try { mediaRef.current.currentTime = c.t; } catch { /* noop */ }
                  }
                  setOpenPinId(c.items[0].id);
                }}
                data-testid={`cluster-badge-${c.key}`}
                title={`${c.items.length} annotation${c.items.length > 1 ? 's' : ''} ${c.label} — click to jump`}
                className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                           border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20
                           text-rose-300 hover:text-rose-200 text-[11px] font-medium
                           transition-colors"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                <span className="font-bold">{c.items.length}</span>
                <span className="opacity-90">notes {c.label}</span>
                {unresolved > 0 && unresolved < c.items.length && (
                  <span className="text-[9px] bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded px-1">
                    {unresolved} open
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-1 bg-zinc-900/70 border border-zinc-800 rounded-lg px-2 py-1.5 backdrop-blur-sm">
          <ToolBtn icon={MousePointer}  active={tool==='select'}   onClick={()=>setTool('select')}   label="Select"  testId="tool-select" />
          <div className="w-px h-5 bg-zinc-700 mx-1" />
          <ToolBtn icon={MessageSquarePlus} active={tool==='pin'}  onClick={()=>setTool('pin')}      label="Pin note" testId="tool-pin" />
          <ToolBtn icon={Square}        active={tool==='rect'}     onClick={()=>setTool('rect')}     label="Box"     testId="tool-rect" />
          <ToolBtn icon={MoveUpRight}   active={tool==='arrow'}    onClick={()=>setTool('arrow')}    label="Arrow"   testId="tool-arrow" />
          <ToolBtn icon={Pencil}        active={tool==='freehand'} onClick={()=>setTool('freehand')} label="Draw"    testId="tool-freehand" />
          <div className="flex-1" />
          <span className="text-xs text-zinc-400 font-mono px-2">
            {type === 'video' ? `${fmtTime(currentTime)} / ${fmtTime(duration)}` : 'image'}
          </span>
        </div>
      )}

      {/* Media + canvas overlay */}
      <div ref={containerRef} className="relative bg-black rounded-xl overflow-hidden select-none" style={{aspectRatio:'16/9'}}>
        {type === 'audio' ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 bg-zinc-900">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Play className="w-8 h-8 text-amber-400" />
            </div>
            <audio ref={mediaRef} src={src} controls className="w-4/5 max-w-xl" controlsList="nodownload" data-testid="audio-player" />
            <p className="text-xs text-zinc-500">Audio review — use playback controls above</p>
          </div>
        ) : type === 'image' ? (
          <img
            ref={mediaRef}
            src={src}
            alt="review"
            className="block w-full h-full object-contain bg-black"
            draggable={false}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <video
            ref={mediaRef}
            src={src}
            className="block w-full h-full object-contain bg-black"
            controls={false}
            playsInline
          />
        )}

        {/* SVG overlay for shapes + annotations (not for audio) */}
        {type !== 'audio' && (<>
        <svg
          ref={overlayRef}
          viewBox="0 0 1000 562.5"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
          style={{ cursor: tool === 'select' ? 'default' : 'crosshair', pointerEvents: tool === 'select' ? 'none' : 'all', zIndex: 10 }}
          onMouseDown={startDraw}
          onMouseMove={moveDraw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
        >
          {/* Existing annotations */}
          {visibleAnnotations.map((a, idx) => (
            <AnnotationLayer key={a.id} ann={a} index={idx+1} openId={openPinId} setOpenId={setOpenPinId} onResolve={onResolve} onDelete={onDelete} viewerRole={viewerRole} />
          ))}
          {/* Draft shape preview */}
          {draft && <ShapePreview shape={draft} />}
        </svg>

        {/* Video scrubber with annotation markers — TWO-ROW layout so the
            transport controls (play/pause/rewind/step) never sit underneath
            an annotation pin, AND artists/clients can scrub frame-by-frame
            since animatic critique happens at the frame level. */}
        {type === 'video' && (
          <div className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-black/95 via-black/75 to-transparent pt-10 pb-2 px-3">
            {/* ROW 1 — Annotation pin track + timeline scrubber.
                Annotation pins live ABOVE the scrubber so they never overlap
                the play/transport buttons in row 2. */}
            <div className="relative h-5 mb-1.5">
              {/* Pin track — clickable rail, jump video to pin's timecode */}
              {annotations.filter(a => a.timecode_sec != null).map(a => {
                const left = duration ? (a.timecode_sec / duration) * 100 : 0;
                const isOpen = openPinId === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    title={`${fmtTime(a.timecode_sec)} — ${(a.text || 'note').slice(0, 80)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (mediaRef.current) mediaRef.current.currentTime = a.timecode_sec;
                      setOpenPinId(a.id);
                    }}
                    data-testid={`timeline-pin-${a.id}`}
                    className={`absolute top-1 -translate-x-1/2 h-3 w-3 rounded-full ring-2 ring-black hover:scale-125 transition-transform cursor-pointer
                                ${a.resolved ? 'bg-emerald-400' : 'bg-rose-400'}
                                ${isOpen ? 'scale-125 ring-amber-300' : ''}`}
                    style={{ left: `${left}%` }}
                  />
                );
              })}
            </div>
            {/* Scrubber rail — sits OUTSIDE the pin track, so clicking it
                seeks the playhead without disturbing pins. */}
            <div className="relative h-1.5 bg-white/20 rounded-full cursor-pointer mb-2"
                 onClick={(e) => {
                   const r = e.currentTarget.getBoundingClientRect();
                   const pct = (e.clientX - r.left) / r.width;
                   if (mediaRef.current && duration) mediaRef.current.currentTime = pct * duration;
                 }}>
              <div className="absolute left-0 top-0 bottom-0 bg-amber-400 rounded-full"
                   style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
              {/* Playhead bead */}
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-white shadow-lg pointer-events-none"
                   style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
            </div>
            {/* ROW 2 — Transport controls. Each has its own slot, no overlap. */}
            <div className="flex items-center gap-2">
              <TransportBtn
                onClick={() => { if (mediaRef.current) mediaRef.current.currentTime = 0; }}
                title="Jump to start (Home)"
                testId="video-jump-start"
              ><SkipBack className="h-3.5 w-3.5" /></TransportBtn>
              <TransportBtn
                onClick={() => { const v = mediaRef.current; if (v) v.currentTime = Math.max(0, v.currentTime - 5); }}
                title="Back 5s (J or ←×5)"
                testId="video-back-5"
              ><Rewind className="h-3.5 w-3.5" /></TransportBtn>
              <TransportBtn
                onClick={() => { const v = mediaRef.current; if (v) v.currentTime = Math.max(0, v.currentTime - (1 / 24)); }}
                title="Previous frame (←)"
                testId="video-prev-frame"
              ><ChevronLeft className="h-4 w-4" /></TransportBtn>
              <button
                onClick={() => { const v = mediaRef.current; if (!v) return; if (v.paused) v.play(); else v.pause(); }}
                className="h-9 w-9 rounded-full bg-white hover:bg-amber-200 text-zinc-900 flex items-center justify-center shadow-lg transition-colors"
                data-testid="video-play-toggle"
                title={playing ? 'Pause (Space / K)' : 'Play (Space / K)'}
              >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
              </button>
              <TransportBtn
                onClick={() => { const v = mediaRef.current; if (v) v.currentTime = Math.min(duration || 0, v.currentTime + (1 / 24)); }}
                title="Next frame (→)"
                testId="video-next-frame"
              ><ChevronRight className="h-4 w-4" /></TransportBtn>
              <TransportBtn
                onClick={() => { const v = mediaRef.current; if (v) v.currentTime = Math.min(duration || 0, v.currentTime + 5); }}
                title="Forward 5s (L)"
                testId="video-fwd-5"
              ><FastForward className="h-3.5 w-3.5" /></TransportBtn>
              <TransportBtn
                onClick={() => { if (mediaRef.current && duration) mediaRef.current.currentTime = duration; }}
                title="Jump to end (End)"
                testId="video-jump-end"
              ><SkipForward className="h-3.5 w-3.5" /></TransportBtn>
              <span className="ml-auto text-xs text-white/80 font-mono tabular-nums" data-testid="video-timecode">
                {fmtTime(currentTime)} / {fmtTime(duration)}
              </span>
            </div>
          </div>
        )}
        </>)}
      </div>

      {/* Note composer */}
      {noteDraft && !readOnly && (
        <div className="bg-zinc-900 border border-amber-400/40 rounded-lg p-3 shadow-xl" data-testid="note-composer">
          <div className="flex items-center gap-2 mb-2 text-xs text-amber-300">
            <Circle className="h-3 w-3 fill-amber-400 stroke-amber-400" />
            New note
            {noteDraft.timecode_sec != null && (
              <span className="font-mono bg-amber-400/10 border border-amber-400/30 rounded px-1.5">@ {fmtTime(noteDraft.timecode_sec)}</span>
            )}
            {noteDraft.shapes?.length > 0 && <span className="text-zinc-500">· {noteDraft.shapes.length} markup</span>}
            {noteDraft.point && <span className="text-zinc-500">· pin</span>}
          </div>
          <textarea
            autoFocus
            value={noteDraft.text}
            onChange={(e)=>setNoteDraft({...noteDraft, text: e.target.value})}
            placeholder="Write your feedback… (e.g. 'Eyes feel too wide around the blink', 'Shadows too cold in BG')"
            className="w-full h-20 bg-zinc-950 border border-zinc-700 rounded text-sm text-zinc-100 p-2 focus:border-amber-400 outline-none"
            data-testid="note-text"
          />
          <div className="flex justify-between gap-2 mt-2">
            <label className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white bg-zinc-800 rounded cursor-pointer flex items-center gap-1">
              <Paperclip className="h-3 w-3" /> Attach file
              <input type="file" className="hidden" accept="image/*,video/*,audio/*" onChange={(e) => {
                if (e.target.files?.[0]) {
                  const f = e.target.files[0];
                  setNoteDraft(prev => ({ ...prev, attachment: f, attachmentName: f.name }));
                }
              }} />
            </label>
            <div className="flex gap-2">
              <button onClick={()=>setNoteDraft(null)} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white">Cancel</button>
              <button onClick={saveNote} disabled={!noteDraft.text.trim()} className="px-3 py-1.5 text-xs bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-zinc-900 font-semibold rounded flex items-center gap-1" data-testid="note-submit">
                <Check className="h-3.5 w-3.5" /> Post note
              </button>
            </div>
          </div>
          {noteDraft.attachmentName && (
            <div className="mt-1 flex items-center gap-2 text-[10px] text-emerald-400 bg-emerald-500/10 rounded px-2 py-1">
              <Paperclip className="w-3 h-3" /> {noteDraft.attachmentName}
              <button onClick={() => setNoteDraft(prev => ({ ...prev, attachment: null, attachmentName: null }))} className="text-zinc-400 hover:text-red-400 ml-auto"><X className="w-3 h-3" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── SUB-COMPONENTS ────────────────────────────────────────── */

function TransportBtn({ onClick, title, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className="h-7 w-7 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
    >{children}</button>
  );
}

function ToolBtn({ icon: Icon, active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      title={label}
      data-testid={testId}
      className={`h-8 w-8 rounded flex items-center justify-center transition-colors ${active ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function ShapePreview({ shape }) {
  const V = 1000, H = 562.5;
  if (shape.kind === 'rect') {
    const x = Math.min(shape.x1, shape.x2)*V, y = Math.min(shape.y1, shape.y2)*H;
    const w = Math.abs(shape.x2-shape.x1)*V, h = Math.abs(shape.y2-shape.y1)*H;
    return <rect x={x} y={y} width={w} height={h} fill="none" stroke={shape.color} strokeWidth="3" strokeDasharray="6 4" />;
  }
  if (shape.kind === 'arrow') {
    return <ArrowLine x1={shape.x1*V} y1={shape.y1*H} x2={shape.x2*V} y2={shape.y2*H} color={shape.color} dashed />;
  }
  if (shape.kind === 'freehand') {
    const d = shape.points.map((p,i)=>`${i===0?'M':'L'}${p.x*V},${p.y*H}`).join(' ');
    return <path d={d} fill="none" stroke={shape.color} strokeWidth="3" strokeLinecap="round" />;
  }
  return null;
}

function AnnotationLayer({ ann, index, openId, setOpenId, onResolve, onDelete, viewerRole = '' }) {
  const V = 1000, H = 562.5;
  const open = openId === ann.id;
  const colorDot = ann.resolved ? '#34d399' : '#fb7185';

  return (
    <g opacity={ann.resolved ? 0.55 : 1}>
      {/* shapes */}
      {(ann.shapes || []).map((s, i) => {
        if (s.kind === 'rect') {
          const x = Math.min(s.x1,s.x2)*V, y = Math.min(s.y1,s.y2)*H;
          const w = Math.abs(s.x2-s.x1)*V, h = Math.abs(s.y2-s.y1)*H;
          return <rect key={i} x={x} y={y} width={w} height={h} fill="none" stroke={s.color||'#fb7185'} strokeWidth="3" />;
        }
        if (s.kind === 'arrow') return <ArrowLine key={i} x1={s.x1*V} y1={s.y1*H} x2={s.x2*V} y2={s.y2*H} color={s.color||'#fb7185'} />;
        if (s.kind === 'freehand') {
          const d = (s.points||[]).map((p,i)=>`${i===0?'M':'L'}${p.x*V},${p.y*H}`).join(' ');
          return <path key={i} d={d} fill="none" stroke={s.color||'#fb7185'} strokeWidth="3" strokeLinecap="round" />;
        }
        return null;
      })}

      {/* pin marker */}
      {ann.point && (
        <g transform={`translate(${ann.point.x*V}, ${ann.point.y*H})`} style={{cursor:'pointer'}} onClick={(e)=>{e.stopPropagation(); setOpenId(open?null:ann.id);}}>
          <circle r="14" fill={colorDot} stroke="white" strokeWidth="2" />
          <text textAnchor="middle" dominantBaseline="central" fontSize="14" fontWeight="700" fill="white">{index}</text>
        </g>
      )}

      {/* popover when open — auto-flip vertically + horizontally so it
          never overlaps the transport bar at the bottom or the right
          edge of the frame. The bottom of the SVG is occupied by the
          transport controls (~95px), so we reserve that area. */}
      {open && (() => {
        const POP_W = 280;
        const POP_H = 160;
        const TRANSPORT_H = 100; // reserved bottom area = scrubber + transport row
        const px = (ann.point?.x ?? 0.5) * V;
        const py = (ann.point?.y ?? 0.5) * H;
        // Horizontal: prefer right of pin, flip left if it would overflow.
        const x = px + 20 + POP_W <= V ? px + 20 : Math.max(0, px - POP_W - 20);
        // Vertical: prefer below pin. If that would overlap the transport
        // bar, flip and render the popover ABOVE the pin. If even above
        // overflows the top, clamp to top edge.
        const wantBelow = py - 20;
        const wouldOverlapTransport = wantBelow + POP_H > H - TRANSPORT_H;
        let y;
        if (!wouldOverlapTransport) {
          y = wantBelow;
        } else {
          const above = py - POP_H - 20;
          y = above >= 0 ? above : 0;
        }
        return (
          <foreignObject x={x} y={y} width={POP_W} height={POP_H}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-2xl text-xs text-zinc-100" data-testid={`annotation-popover-${ann.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">{displayNameFor(ann, viewerRole)}</span>
                {shouldRenderHandlePill(ann, viewerRole) ? <span className="font-mono text-cyan-400 text-[10px]">@{handleFor(ann)}</span> : null}
                <span className="text-[10px] text-zinc-500">{(ann.author_role||'').replace('_',' ')}</span>
                {ann.timecode_sec != null && <span className="ml-auto font-mono text-[10px] text-amber-300">@ {fmtShort(ann.timecode_sec)}</span>}
              </div>
              <div className="text-zinc-300 whitespace-pre-wrap">{ann.text}</div>
              <div className="flex gap-1 mt-2 pt-2 border-t border-zinc-800">
                {!ann.resolved && <button onClick={(e)=>{e.stopPropagation(); onResolve && onResolve(ann.id);}} className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1"><Check className="h-3 w-3"/>Resolve</button>}
                {onDelete && <button onClick={(e)=>{e.stopPropagation(); onDelete(ann.id);}} className="text-[11px] text-rose-400 hover:text-rose-300 ml-auto flex items-center gap-1"><Trash2 className="h-3 w-3"/>Delete</button>}
              </div>
            </div>
          </foreignObject>
        );
      })()}
    </g>
  );
}

function ArrowLine({ x1, y1, x2, y2, color, dashed }) {
  const id = `ah-${Math.floor(Math.random()*1e6)}`;
  return (
    <g>
      <defs>
        <marker id={id} markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <path d="M0,0 L0,6 L9,3 z" fill={color} />
        </marker>
      </defs>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="3" strokeDasharray={dashed?'6 4':''} markerEnd={`url(#${id})`} />
    </g>
  );
}

function fmtShort(t) {
  const m = Math.floor(t/60).toString().padStart(2,'0');
  const s = Math.floor(t%60).toString().padStart(2,'0');
  return `${m}:${s}`;
}
